import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import ClientV2Bridge from './ClientV2Bridge';
import {
  getClient,
  getClientDetails,
  initPhase,
  clearLastAddedClient,
  enableTracking,
  updateClientData,
  updateFactSheet,
  updateViewingClient
} from '../../../store/client/duck';
import { connectedUserPolicies, fetchFigureAdviser, getAdviserDashboard } from '../../../store/adviserDashboard/duck';
import { mergeDialog, toggleControl, toggleDialog } from '../../../store/control/duck';
import { resetAnalysisDefaultValues } from '../../../store/analysis/duck';
import { getNotificationCenterList, clearAllMsgOpt } from '../../../store/notificationCenter/duck';
import { getUser, initPhaseEmail } from '../../../store/user/duck';
import { LOADING } from '../../../constants/phase';
import { peerFigureOf, withClientScoreLevels } from './clientScores';
import type { QueueClient } from '../../../types/client';
import { movePolicy } from '../../../store/policy/duck';
import {
  hasCampaignCode,
  shouldEnableAiCoach,
  shouldEnableChatGPT,
  shouldEnableRecruitmentMode,
  shouldEnableTeamChatBot,
  showShowTeamLibrary
} from '../../../utils/user';

// `scoreLevel` is rebuilt from the array the store already holds, so a client
// refreshed by GET_CLIENT_BY_ID (which cannot compute the band) no longer shows
// a stale CPI. Done here rather than in the page so the bridge sees the same
// corrected array — A.I. 精選 picks its target off `clientData` too. One-entry
// cache: the store hands back the same array reference until it actually
// changes, and mapStateToProps runs on every dispatch.
let lastRawClients: QueueClient[] | null = null;
let lastScoredClients: QueueClient[] = [];
const scoredClientsOf = (raw: QueueClient[]): QueueClient[] => {
  if (raw !== lastRawClients) {
    lastRawClients = raw;
    lastScoredClients = withClientScoreLevels(raw);
  }
  return lastScoredClients;
};

// Deliberately lean: the V1 page maps 67 props. This page shares no connect
// config with V1, so a change here cannot alter the old page's behaviour.
const mapStateToProps = (state: any) => ({
  clientData: scoredClientsOf(_.get(state, 'client.clientData', [])),
  // Drives the refresh control's spinner, and keeps a page mount from firing a
  // second fetch on top of the one login/boot already started.
  fetchingClients: _.get(state, 'client.fetchClientPhase') === LOADING,
  // Whether the last factsheet write landed. The drawer answers its 補資料 and
  // 客戶狀態 questions optimistically — it has to, since one tap costs three
  // round trips — so it needs to be told when the write it echoed did not
  // actually happen. Everything else in the app that edits a factsheet already
  // reads this (clientFactsheet.js passes it to all 35 of its fields;
  // SmartIdeaDialog watches the same transitions); this page was the one editor
  // that dropped a failure on the floor and left the echo standing.
  updateFactSheetPhase: _.get(state, 'client.updateFactSheetPhase'),
  // The phase above cannot say WHICH write it belongs to, and the drawer lets an
  // adviser answer four questions faster than one round trip — so two writes
  // overlap routinely and the phase describes only the last to settle. These two
  // are what the bridge actually watches (store/client/duck.js): a count of the
  // writes still in the air, and a monotonic tally of the ones that failed. A
  // failure whose sibling succeeded first used to leave no trace at all here.
  pendingFactWrites: _.get(state, 'client.pendingFactWrites', 0),
  factWriteFailureVersion: _.get(state, 'client.factWriteFailureVersion', 0),
  policyDue: _.get(state, 'adviserDashboard.GetUsersPolicyDueData', []),
  // Coverage-gap membership for the life/illness/medical/saving/ilas list
  // states. Populated by the dashboard's connectedUserPolicies() dispatch; the
  // page refetches it itself when one of those states arrives without it.
  policiesData: _.get(state, 'adviserDashboard.policiesData'),
  // Clients whose birthday has just passed — drives dateBackBirthdayCount.
  // Filled by the dashboard's getAdviserDashboard(); fetched on demand here.
  pastBirthdayUsers: _.get(state, 'adviserDashboard.PastBirthdayOfUsers'),
  userEmail: _.get(state, 'user.userDetails.email'),
  currency: _.get(state, 'user.userDetails.currency', 'HKD'),
  families: _.get(state, 'user.userDetails.families', []),
  usedTags: _.get(state, 'user.userDetails.usedTags', []),
  potentialCount: _.get(state, 'referral.newReferralCount', 0),
  notificationCount: _.get(state, 'notificationCenter.newNotificationNumber', 0),
  // Badge visibility for the top-nav bell (NotificationBellButton), mirroring
  // topNavigation.js's own mapStateToProps — newNotificationNumber alone can
  // be 0 while a non-numeric notification is still pending.
  hasNotifications: _.get(state, 'notificationCenter.hasNotifications', false),
  clientsToSendNum: _.get(state, 'notificationCenter.clientsToSendNum', 0),
  clearAllThenFetchClient: _.get(state, 'notificationCenter.clearAllThenFetchClient', false),
  movePolicyStatus: _.get(state, 'policy.movePolicyStatus', ''),
  // No `todoCount` here: the ToDo button lives in the top navigation bar, which
  // has its own container for it. Computing it here meant re-filtering both
  // adviserTodo arrays by due date on every dispatch to feed a prop the command
  // bar does not accept.
  // `state.figure`, not `state.user.figure` — see peerFigureOf. Getting this
  // wrong cost every client the 排名 axis of the drawer's coverage radar.
  figure: peerFigureOf(state),
  // The client this session has just created. Set by the reducer that inserts
  // them (GET_CLIENT_BY_ID_SUCCESS) while the adviser is still on the add form,
  // so it is the only way this page can learn about an add that happened before
  // it mounted.
  lastAddedClientId: _.get(state, 'client.lastAddedClientId', null),
  // Recruitment tool access is resolved here, but unavailable cards remain in
  // the drawer as grey placeholders so the Business/Recruitment layouts keep a
  // stable shape. AI Coach accepts both campaign-code generations: the older
  // Linker entry calls it AI Drill (FEATURE_AIDRILL), while Home uses the newer
  // AI Coach name (FEATURE_AICOACH); both open the same route.
  teamHubAvailable: showShowTeamLibrary({
    user: _.get(state, 'user.userDetails'),
    teamCampaignCodes: _.get(state, 'functions.campaignCodes', [])
  }),
  // A.I. 智能助理 (/gpt-options): FEATURE_GPT or an Ultra plan — the same gate
  // V1's client page shows its A.I. button by, and the one the page itself
  // enforces by sending everyone else back to /home.
  aiAvailable: shouldEnableChatGPT({
    user: _.get(state, 'user.userDetails'),
    teamCampaignCodes: _.get(state, 'functions.campaignCodes', []),
    userSubscription: _.get(state, 'userSubscription.userSubscription')
  }),
  teamBotAvailable: shouldEnableTeamChatBot({
    user: _.get(state, 'user.userDetails'),
    teamCampaignCodes: _.get(state, 'functions.campaignCodes', []),
    userSubscription: _.get(state, 'userSubscription.userSubscription')
  }),
  aiCoachAvailable:
    shouldEnableAiCoach({
      user: _.get(state, 'user.userDetails'),
      teamCampaignCodes: _.get(state, 'functions.campaignCodes', [])
    }) ||
    !!hasCampaignCode(_.get(state, 'user.userDetails'), _.get(state, 'functions.campaignCodes', []), 'FEATURE_AIDRILL'),
  // 招募模式's campaign-code gate, resolved to one boolean here rather than
  // handed down as two raw arrays — the drawer decides what to render, not who
  // this adviser is. `functions.campaignCodes` is the TEAM's codes, so a pilot
  // team is opened in one place; see shouldEnableRecruitmentMode.
  recruitmentMode: shouldEnableRecruitmentMode({
    user: _.get(state, 'user.userDetails'),
    teamCampaignCodes: _.get(state, 'functions.campaignCodes', [])
  }),
  userDetails: _.get(state, 'user.userDetails')
});

export default injectIntl(
  connect(mapStateToProps, {
    getClient,
    fetchFigureAdviser,
    connectedUserPolicies,
    getAdviserDashboard,
    toggleDialog,
    mergeDialog,
    toggleControl,
    updateFactSheet,
    updateClientData,
    updateViewingClient,
    getClientDetails,
    enableTracking,
    resetAnalysisDefaultValues,
    initPhase,
    clearLastAddedClient,
    getNotificationCenterList,
    clearAllMsgOpt,
    getUser,
    initPhaseEmail,
    movePolicy
  })(ClientV2Bridge as any)
);
